import { useDeepMemoValue } from '@cotera/client/app/hooks/deep-memo';
import Fuse, { IFuseOptions } from 'fuse.js';
import { useCallback, useMemo } from 'react';

/** A field to match on, optionally weighted so some fields outrank others. */
export type FuzzyKey<T> = keyof T | { name: keyof T; weight: number };

export const useFuzzySearch = function <T>(
  items: T[],
  keys: FuzzyKey<T>[],
  options?: IFuseOptions<T>
): (query: string | null) => T[] {
  // `keys` and `options` are nearly always fresh literals at the call site, so
  // they are compared by value. `items` is compared by reference, which is what
  // callers already memoize for. The alternative — deep-comparing the *built
  // index*, which is what this hook used to do — walked every posting list of
  // both the old and the new index on every single render.
  const stableKeys = useDeepMemoValue(keys);
  const stableOptions = useDeepMemoValue(options);

  const fuse = useMemo(
    () =>
      new Fuse<T>(items, {
        keys: stableKeys.map((key) =>
          typeof key === 'object'
            ? { name: String(key.name), weight: key.weight }
            : String(key)
        ),
        ...(stableOptions ?? {}),
      }),
    [items, stableKeys, stableOptions]
  );

  return useCallback(
    (query: string | null) => {
      if (query === null || query.trim().length === 0) {
        return items;
      }

      const tokens = query.trim().split(/\s+/);

      if (tokens.length === 1) {
        return fuse.search(query).map((x) => x.item);
      }

      // Fuse matches the query as a single pattern, so word order counts:
      // "chat new" will not find "New Chat". Multi-word queries are therefore
      // searched a token at a time and intersected — an item has to match every
      // token, and its rank is the sum of its per-token ranks, so the item that
      // sits near the top for each word wins.
      let surviving: Map<T, number> | null = null;

      for (const token of tokens) {
        const ranks = new Map<T, number>();
        fuse.search(token).forEach((hit, rank) => ranks.set(hit.item, rank));

        if (surviving === null) {
          surviving = ranks;
          continue;
        }

        const intersection = new Map<T, number>();
        surviving.forEach((rank, item) => {
          const tokenRank = ranks.get(item);
          if (tokenRank !== undefined) {
            intersection.set(item, rank + tokenRank);
          }
        });
        surviving = intersection;
      }

      return [...(surviving?.entries() ?? [])]
        .sort(([, a], [, b]) => a - b)
        .map(([item]) => item);
    },
    [fuse, items]
  );
};
